import { buildEditorAppearance } from './editor_appearance'

// Preserve the editor selection for pointer users and support native keyboard activation.
export function bindToolbarButton(button, action) {
  if (button.title) button.setAttribute("aria-label", button.title)
  button.addEventListener("mousedown", event => event.preventDefault())
  button.addEventListener("click", action)
}

// Shared toolbar builder for Tiptap editors.
// Used by both tiptap_editor_controller (single doc) and section_editor_controller (sectioned docs).
// Commands dispatch to the getEditor() function, which returns the currently active editor.

export function buildToolbar(container, getEditor, { beforePrint = () => true, onFind } = {}) {
  const toolbar = document.createElement("div")
  toolbar.className = "tiptap-fixed-toolbar"

  const groups = [
    [
      { cmd: "bold", icon: "fa-bold", title: "Bold (⌘B)" },
      { cmd: "italic", icon: "fa-italic", title: "Italic (⌘I)" },
      { cmd: "underline", icon: "fa-underline", title: "Underline (⌘U)" },
      { cmd: "strike", icon: "fa-strikethrough", title: "Strikethrough" },
      { cmd: "code", icon: "fa-code", title: "Code" },
    ],
    [
      { cmd: "headingDropdown", icon: "fa-heading", title: "Headings", dropdown: true },
      { cmd: "blockquote", icon: "fa-quote-left", title: "Quote" },
    ],
    [
      { cmd: "bulletList", icon: "fa-list-ul", title: "Bullet List" },
      { cmd: "orderedList", icon: "fa-list-ol", title: "Ordered List" },
      { cmd: "taskList", icon: "fa-square-check", title: "Task List / Checklist" },
    ],
    [
      { cmd: "subscript", icon: "fa-subscript", title: "Subscript" },
      { cmd: "superscript", icon: "fa-superscript", title: "Superscript" },
    ],
    [
      { cmd: "alignLeft", icon: "fa-align-left", title: "Align Left" },
      { cmd: "alignCenter", icon: "fa-align-center", title: "Align Center" },
      { cmd: "alignRight", icon: "fa-align-right", title: "Align Right" },
      { cmd: "alignJustify", icon: "fa-align-justify", title: "Justify" },
    ],
    [
      { cmd: "link", icon: "fa-link", title: "Link (⌘K)" },
      { cmd: "image", icon: "fa-image", title: "Insert Image" },
      { cmd: "table", icon: "fa-table", title: "Insert Table" },
      { cmd: "horizontalRule", icon: "fa-minus", title: "Horizontal Rule" },
    ],
    [
      { cmd: "clearFormatting", icon: "fa-text-slash", title: "Clear Formatting" },
      { cmd: "undo", icon: "fa-undo", title: "Undo (⌘Z)" },
      { cmd: "redo", icon: "fa-redo", title: "Redo (⌘⇧Z)" },
      { cmd: "print", icon: "fa-print", title: "Print" },
      ...(onFind ? [{ cmd: "find", icon: "fa-search", title: "Find in document (⌘F)" }] : []),
    ],
  ]

  const cleanup = []
  cleanup.push(buildEditorAppearance())
  const state = { linkFormEl: null, toolbar, beforePrint, onFind }

  groups.forEach((group, gi) => {
    if (gi > 0) {
      const sep = document.createElement("div")
      sep.className = "tiptap-toolbar-sep"
      toolbar.appendChild(sep)
    }
    group.forEach(btn => {
      if (btn.dropdown && btn.cmd === "headingDropdown") {
        toolbar.appendChild(buildHeadingDropdown(getEditor, cleanup))
      } else {
        const el = document.createElement("button")
        el.type = "button"
        el.className = "tiptap-toolbar-btn"
        el.title = btn.title
        el.dataset.cmd = btn.cmd
        el.innerHTML = `<span class="fas ${btn.icon}"></span>`
        bindToolbarButton(el, (e) => {
          e.preventDefault()
          executeToolbarCommand(btn.cmd, getEditor, state)
        })
        toolbar.appendChild(el)
      }
    })
  })

  // "More" toggle button — only visible when buttons overflow one row
  const moreBtn = document.createElement("button")
  moreBtn.type = "button"
  moreBtn.className = "tiptap-toolbar-more"
  moreBtn.title = "More formatting options"
  moreBtn.setAttribute("aria-expanded", "false")
  moreBtn.style.display = "none"
  moreBtn.innerHTML = '<span class="fas fa-ellipsis-h"></span>'
  bindToolbarButton(moreBtn, (e) => {
    e.preventDefault()
    toolbar.classList.toggle("toolbar-expanded")
    moreBtn.classList.toggle("is-active")
    moreBtn.setAttribute("aria-expanded", String(toolbar.classList.contains("toolbar-expanded")))
    checkOverflow()
  })
  toolbar.appendChild(moreBtn)

  // Show/hide "more" button based on whether content overflows one row
  const checkOverflow = () => {
    if (!toolbar.isConnected || !toolbar.clientWidth) return
    const compact = toolbar.closest('[data-controller~="editor-chrome"]') && window.matchMedia('(max-width: 1399px)').matches
    if (compact) {
      layoutCompactToolbar(toolbar, moreBtn)
      return
    }
    toolbar.classList.remove("toolbar-has-overflow", "toolbar-measuring")
    toolbar.querySelectorAll(".toolbar-overflow-item").forEach(item => item.classList.remove("toolbar-overflow-item"))
    const isExpanded = toolbar.classList.contains("toolbar-expanded")
    if (isExpanded) return // don't hide while expanded
    const hasOverflow = toolbar.scrollHeight > toolbar.clientHeight + 4
    moreBtn.style.display = hasOverflow ? "" : "none"
    toolbar.style.paddingRight = hasOverflow ? "42px" : "16px"
    toolbar.style.justifyContent = hasOverflow ? "flex-start" : "center"
  }
  // Check after layout settles and on resize
  const overflowFrame = requestAnimationFrame(checkOverflow)
  const overflowTimeout = setTimeout(checkOverflow, 200)
  let overflowResizeFrame
  const overflowObserver = new ResizeObserver(() => {
    cancelAnimationFrame(overflowResizeFrame)
    overflowResizeFrame = requestAnimationFrame(checkOverflow)
  })
  overflowObserver.observe(toolbar)
  window.addEventListener("resize", checkOverflow)

  // Link input popover (floats below toolbar, not inline)
  state.linkFormEl = document.createElement("div")
  state.linkFormEl.className = "tiptap-link-popover"
  state.linkFormEl.style.display = "none"
  state.linkFormEl.innerHTML = `
    <div class="tiptap-link-popover-inner">
      <input type="text" aria-label="Link URL" placeholder="Paste or type a link" class="tiptap-link-input">
      <button type="button" class="tiptap-link-submit" title="Apply"><span class="fas fa-check"></span></button>
      <button type="button" class="tiptap-link-remove" title="Remove link"><span class="fas fa-times"></span></button>
    </div>
  `


  const input = state.linkFormEl.querySelector(".tiptap-link-input")
  const submit = state.linkFormEl.querySelector(".tiptap-link-submit")
  const remove = state.linkFormEl.querySelector(".tiptap-link-remove")

  bindToolbarButton(submit, (e) => {
    e.preventDefault()
    const editor = getEditor()
    const url = input.value.trim()
    if (url && editor) editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run()
    state.linkFormEl.style.display = "none"
  })
  bindToolbarButton(remove, (e) => {
    e.preventDefault()
    const editor = getEditor()
    if (editor) editor.chain().focus().extendMarkRange("link").unsetLink().run()
    state.linkFormEl.style.display = "none"
  })
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") { e.preventDefault(); submit.click() }
    else if (e.key === "Escape") { state.linkFormEl.style.display = "none"; getEditor()?.commands.focus() }
  })
  // Close when clicking outside
  const closeLink = (e) => {
    if (state.linkFormEl.style.display !== "none" && !state.linkFormEl.contains(e.target)) {
      state.linkFormEl.style.display = "none"
    }
  }
  document.addEventListener("mousedown", closeLink)
  container.insertBefore(toolbar, container.firstChild)
  toolbar.after(state.linkFormEl)
  const printStatus = document.createElement("div")
  printStatus.className = "tiptap-print-status"
  printStatus.setAttribute("role", "status")
  printStatus.setAttribute("aria-live", "polite")
  printStatus.hidden = true
  toolbar.after(printStatus)
  const setPrintStatus = message => { printStatus.textContent = message; printStatus.hidden = !message }
  const tableToolbar = buildTableToolbar(container, getEditor)

  return { toolbar, state, tableToolbar, setPrintStatus, destroy() {
    cancelAnimationFrame(overflowFrame)
    cancelAnimationFrame(overflowResizeFrame)
    overflowObserver.disconnect()
    clearTimeout(overflowTimeout)
    window.removeEventListener("resize", checkOverflow)
    document.removeEventListener("mousedown", closeLink)
    cleanup.forEach(fn => fn())
    state.linkFormEl.remove()
    tableToolbar.remove()
    printStatus.remove()
    toolbar.remove()
  } }

}

function layoutCompactToolbar(toolbar, moreBtn) {
  // Measure the actual column, including when a sidebar changes its width.
  toolbar.classList.add("toolbar-measuring")
  toolbar.classList.remove("toolbar-has-overflow")
  const items = Array.from(toolbar.children).filter(item => item.matches(".tiptap-toolbar-btn, .tiptap-toolbar-dropdown-wrapper"))
    .sort((a, b) => Number(getComputedStyle(a).order) - Number(getComputedStyle(b).order))
  const widths = items.map(item => item.getBoundingClientRect().width)
  const availableWidth = () => {
    const style = getComputedStyle(toolbar)
    return toolbar.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)
  }
  const hasOverflow = widths.reduce((sum, width) => sum + width, 0) > availableWidth() + 0.5
  toolbar.classList.toggle("toolbar-has-overflow", hasOverflow)
  moreBtn.style.display = hasOverflow ? "" : "none"
  if (!hasOverflow) {
    toolbar.classList.remove("toolbar-expanded")
    moreBtn.classList.remove("is-active")
    moreBtn.setAttribute("aria-expanded", "false")
  }
  const available = availableWidth()
  let used = 0
  items.forEach((item, index) => {
    used += widths[index]
    item.classList.toggle("toolbar-overflow-item", hasOverflow && used > available + 0.5)
  })
  toolbar.classList.remove("toolbar-measuring")
  // Resizing must not strand keyboard focus on a newly hidden command.
  if (!toolbar.classList.contains("toolbar-expanded") && items.some(item => item.classList.contains("toolbar-overflow-item") && item.contains(document.activeElement))) {
    moreBtn.focus({ preventScroll: true })
  } else if (!hasOverflow && document.activeElement === moreBtn) {
    (items[0].matches("button") ? items[0] : items[0].querySelector("button"))?.focus({ preventScroll: true })
  }
}

function buildHeadingDropdown(getEditor, cleanup) {
  const wrapper = document.createElement("div")
  wrapper.className = "tiptap-toolbar-dropdown-wrapper"
  wrapper.style.cssText = "position: relative; display: inline-flex;"

  const el = document.createElement("button")
  el.type = "button"
  el.className = "tiptap-toolbar-btn"
  el.title = "Headings"
  el.dataset.cmd = "headingDropdown"
  el.innerHTML = `<span class="fas fa-heading"></span><span class="fas fa-caret-down" style="font-size: 8px; margin-left: 2px;"></span>`

  const dropdown = document.createElement("div")
  dropdown.className = "tiptap-heading-dropdown"
  dropdown.style.display = "none"

  const headings = [
    { level: 1, label: "Heading 1", size: "20px" },
    { level: 2, label: "Heading 2", size: "17px" },
    { level: 3, label: "Heading 3", size: "15px" },
    { level: 4, label: "Heading 4", size: "14px" },
    { label: "Paragraph", size: "16px", isParagraph: true },
  ]

  const setOpen = open => {
    const toolbar = wrapper.closest(".tiptap-fixed-toolbar")
    // A scrolling More tray would clip this popover. Return to the primary row
    // before opening headings on compact editors, preserving the selection.
    if (open && toolbar?.closest('[data-controller~="editor-chrome"]') && window.matchMedia('(max-width: 1399px)').matches) {
      toolbar.classList.remove('toolbar-expanded')
      const more = toolbar.querySelector('.tiptap-toolbar-more')
      more?.classList.remove('is-active')
      more?.setAttribute('aria-expanded', 'false')
    }
    dropdown.style.display = open ? "block" : "none"
    el.setAttribute("aria-expanded", String(open))
    toolbar?.classList.toggle("toolbar-popover-open", open)
  }
  el.setAttribute("aria-expanded", "false")
  wrapper.addEventListener("keydown", event => {
    if (event.key === "Escape") { setOpen(false); el.focus() }
  })
  headings.forEach(h => {
    const opt = document.createElement("button")
    opt.type = "button"
    opt.className = "tiptap-heading-option"
    opt.innerHTML = `<span style="font-size: ${h.size}; font-weight: ${h.isParagraph ? 'normal' : 'bold'};">${h.label}</span>`
    bindToolbarButton(opt, (e) => {
      e.preventDefault()
      e.stopPropagation()
      const editor = getEditor()
      if (!editor) return
      if (h.isParagraph) {
        editor.chain().focus().setParagraph().run()
      } else {
        editor.chain().focus().toggleHeading({ level: h.level }).run()
      }
      setOpen(false)
      updateToolbarState(wrapper.closest(".tiptap-fixed-toolbar"), editor)
    })
    dropdown.appendChild(opt)
  })

  bindToolbarButton(el, (e) => {
    e.preventDefault()
    setOpen(dropdown.style.display === "none")
  })

  const closeHeading = e => { if (!wrapper.contains(e.target)) setOpen(false) }
  document.addEventListener("mousedown", closeHeading)
  cleanup.push(() => document.removeEventListener("mousedown", closeHeading))

  wrapper.appendChild(el)
  wrapper.appendChild(dropdown)
  return wrapper
}

export function executeToolbarCommand(cmd, getEditor, state) {
  if (cmd === "find") {
    const toolbar = state.toolbar
    toolbar?.classList.remove('toolbar-expanded')
    const more = toolbar?.querySelector('.tiptap-toolbar-more')
    more?.classList.remove('is-active')
    more?.setAttribute('aria-expanded', 'false')
    if (toolbar) { toolbar.scrollTop = 0; toolbar.scrollLeft = 0 }
    state.onFind?.()
    return
  }
  // Print doesn't need an active editor
  if (cmd === "print") {
    const delivery = document.getElementById("publishForm")?._documentDelivery
    if (delivery) { delivery.openExport("pdf", document.activeElement); return }
    if (state.beforePrint && !state.beforePrint()) return
    const postId = document.querySelector(".post-form-wrapper")?.getAttribute("data-post-id")
    if (postId) {
      window.open(`/posts/${postId}/download.pdf`, "_blank")
    } else {
      window.print()
    }
    return
  }

  const editor = getEditor()
  if (!editor) return
  const chain = editor.chain().focus()

  switch (cmd) {
    case "bold": chain.toggleBold().run(); break
    case "italic": chain.toggleItalic().run(); break
    case "underline": chain.toggleUnderline().run(); break
    case "strike": chain.toggleStrike().run(); break
    case "code": chain.toggleCode().run(); break
    case "heading1": chain.toggleHeading({ level: 1 }).run(); break
    case "heading2": chain.toggleHeading({ level: 2 }).run(); break
    case "heading3": chain.toggleHeading({ level: 3 }).run(); break
    case "blockquote": chain.toggleBlockquote().run(); break
    case "bulletList": chain.toggleBulletList().run(); break
    case "orderedList": chain.toggleOrderedList().run(); break
    case "horizontalRule": chain.setHorizontalRule().run(); break
    case "subscript": chain.toggleSubscript().run(); break
    case "superscript": chain.toggleSuperscript().run(); break
    case "alignLeft": chain.setTextAlign("left").run(); break
    case "alignCenter": chain.setTextAlign("center").run(); break
    case "alignRight": chain.setTextAlign("right").run(); break
    case "alignJustify": chain.setTextAlign("justify").run(); break
    case "taskList": chain.toggleTaskList().run(); break
    case "table":
      editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
      return
    case "link":
      if (state.linkFormEl) {
        const input = state.linkFormEl.querySelector(".tiptap-link-input")
        input.value = editor.getAttributes("link").href || ""
        state.linkFormEl.style.display = "block"
        setTimeout(() => input.focus(), 10)
      }
      return
    case "image":
      const fileInput = document.createElement("input")
      fileInput.type = "file"
      fileInput.accept = "image/jpeg,image/png,image/gif"
      fileInput.addEventListener("change", () => {
        const file = fileInput.files[0]
        if (!file) return

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          alert("Image is too large. Maximum size is 10 MB.")
          return
        }

        // Unique placeholder to avoid collisions with concurrent uploads
        const uploadId = Date.now() + "-" + Math.random().toString(36).slice(2, 6)
        const placeholderText = `⏳ Uploading image (${uploadId})...`
        editor.chain().focus().insertContent(placeholderText).run()

        const removePlaceholder = () => {
          if (editor.isDestroyed) return null
          const { state } = editor
          let from = null, to = null
          state.doc.descendants((node, pos) => {
            if (node.isText && node.text && node.text.includes(placeholderText)) {
              from = pos + node.text.indexOf(placeholderText); to = from + placeholderText.length; return false
            }
          })
          if (from !== null) editor.chain().deleteRange({ from, to }).run()
          return from
        }

        const formData = new FormData()
        formData.append("files[]", file)
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
        const postSlug = window.location.pathname.match(/posts\/([^/]+)/)?.[1]
          || document.querySelector("input[name='post[uuid]']")?.value
        const uploadUrl = postSlug ? `/posts/${postSlug}/images/upload.json` : "/images/upload.json"

        fetch(uploadUrl, {
          method: "POST",
          headers: { "X-CSRF-Token": csrfToken },
          body: formData,
          credentials: "same-origin",
        })
        .then(res => {
          if (!res.ok) throw new Error(`Upload failed (${res.status})`)
          return res.json()
        })
        .then(data => {
          const url = data.files?.[0]?.url || data.url || data.link
          const from = removePlaceholder()
          if (from !== null && url) {
            editor.chain().focus().setTextSelection(from).setImage({ src: url }).run()
          }
        })
        .catch(err => {
          console.error("[Tiptap] Image upload failed:", err)
          removePlaceholder()
        })
      })
      fileInput.click()
      return
    // print handled above, before editor guard
    case "clearFormatting": chain.unsetAllMarks().clearNodes().run(); break
    case "undo": chain.undo().run(); break
    case "redo": chain.redo().run(); break
  }

  const toolbarEl = state.toolbar
  if (toolbarEl) updateToolbarState(toolbarEl, editor)
}

export function updateToolbarState(toolbarEl, editor) {
  if (!editor || !toolbarEl) return
  updateTableToolbar(toolbarEl.parentElement?.querySelector(".tiptap-table-toolbar"), editor)

  toolbarEl.querySelectorAll(".tiptap-toolbar-btn").forEach(btn => {
    const cmd = btn.dataset.cmd
    if (!cmd) return

    let isActive = false
    switch (cmd) {
      case "bold": isActive = editor.isActive("bold"); break
      case "italic": isActive = editor.isActive("italic"); break
      case "underline": isActive = editor.isActive("underline"); break
      case "strike": isActive = editor.isActive("strike"); break
      case "code": isActive = editor.isActive("code"); break
      case "blockquote": isActive = editor.isActive("blockquote"); break
      case "bulletList": isActive = editor.isActive("bulletList"); break
      case "orderedList": isActive = editor.isActive("orderedList"); break
      case "taskList": isActive = editor.isActive("taskList"); break
      case "subscript": isActive = editor.isActive("subscript"); break
      case "superscript": isActive = editor.isActive("superscript"); break
      case "alignLeft": isActive = editor.isActive({ textAlign: "left" }); break
      case "alignCenter": isActive = editor.isActive({ textAlign: "center" }); break
      case "alignRight": isActive = editor.isActive({ textAlign: "right" }); break
      case "alignJustify": isActive = editor.isActive({ textAlign: "justify" }); break
      case "link": isActive = editor.isActive("link"); break
      case "headingDropdown":
        isActive = editor.isActive("heading")
        break
    }

    btn.classList.toggle("is-active", isActive)
    btn.setAttribute("aria-pressed", String(isActive))
  })
}

function buildTableToolbar(container, getEditor) {
  const tableToolbar = document.createElement("div")
  tableToolbar.className = "tiptap-table-toolbar"
  tableToolbar.style.display = "none"

  // Toggle button
  const toggle = document.createElement("button")
  toggle.type = "button"
  toggle.className = "tiptap-table-toggle"
  toggle.innerHTML = '<span class="fas fa-table"></span> Table Options'
  bindToolbarButton(toggle, (e) => {
    e.preventDefault()
    const expanded = tableOptions.style.display !== "none"
    tableOptions.style.display = expanded ? "none" : "flex"
  })
  tableToolbar.appendChild(toggle)

  // Expandable options panel
  const tableOptions = document.createElement("div")
  tableOptions.className = "tiptap-table-options"
  tableOptions.style.display = "none"

  const buttons = [
    { cmd: "addColumnBefore", icon: "fa-arrow-left", label: "Col Before" },
    { cmd: "addColumnAfter", icon: "fa-arrow-right", label: "Col After" },
    { cmd: "addRowBefore", icon: "fa-arrow-up", label: "Row Above" },
    { cmd: "addRowAfter", icon: "fa-arrow-down", label: "Row Below" },
    { cmd: "toggleHeaderRow", icon: "fa-table-rows", label: "Header" },
    { cmd: "deleteColumn", icon: "fa-columns", label: "Del Col", danger: true },
    { cmd: "deleteRow", icon: "fa-minus", label: "Del Row", danger: true },
    { cmd: "deleteTable", icon: "fa-trash", label: "Delete", danger: true },
  ]

  buttons.forEach(btn => {
    const el = document.createElement("button")
    el.type = "button"
    el.className = `tiptap-table-btn${btn.danger ? " danger" : ""}`
    el.title = btn.label
    el.dataset.tableCmd = btn.cmd
    el.innerHTML = `<span class="fas ${btn.icon}"></span> ${btn.label}`
    bindToolbarButton(el, (e) => {
      e.preventDefault()
      const editor = getEditor()
      if (editor) { editor.chain().focus()[btn.cmd]().run(); updateTableToolbar(tableToolbar, editor) }
    })
    tableOptions.appendChild(el)
  })

  tableToolbar.appendChild(tableOptions)

  container.appendChild(tableToolbar)
  return tableToolbar
}

export function updateTableToolbar(tableToolbar, editor) {
  if (!tableToolbar || !editor || editor.isDestroyed) return
  tableToolbar.style.display = editor.isActive("table") ? "flex" : "none"
  if (!editor.isActive("table")) return
  const dom = editor.view.domAtPos(editor.state.selection.from).node
  const table = (dom.nodeType === Node.ELEMENT_NODE ? dom : dom.parentElement)?.closest("table")
  const container = tableToolbar.parentElement
  if (!table || !container) return
  const tableRect = table.getBoundingClientRect()
  const containerRect = container.getBoundingClientRect()
  tableToolbar.style.position = "absolute"
  tableToolbar.style.top = `${tableRect.top - containerRect.top + container.scrollTop - 36}px`
  tableToolbar.style.left = `${Math.max(0, tableRect.left - containerRect.left)}px`
}
